import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  EnhancedTable,
  StickyTableHeader,
  SortableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
  SavedFiltersDropdown,
  TableSettingsDropdown,
} from '../components/ui/enhanced-table';
import { useTableSort, useSavedFilters, useTableSelection, useTableSettings } from '../hooks/useTableEnhancements';
import { Plus, Pencil, Trash2, DoorOpen, Box, Copy, QrCode, Eye, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import QRLabelPrint from '../components/QRLabelPrint';

const ASSET_CATEGORIES = ['HVAC', 'Electrical', 'Plumbing', 'Mechanical', 'IT', 'Safety', 'General'];
const ASSET_STATUSES = ['operational', 'needs_maintenance', 'out_of_service'];

export default function RoomsAssetsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRTL = i18n.language === 'ar';
  
  const [tab, setTab] = useState(searchParams.get('tab') || 'rooms');
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [assets, setAssets] = useState([]);
  const [fBranch, setFBranch] = useState('all');
  const [fSearch, setFSearch] = useState('');
  const [fCategory, setFCategory] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fRoom, setFRoom] = useState('all');
  const [loading, setLoading] = useState(true);

  // Sorting
  const roomsSort = useTableSort('rooms_page', 'name', 'asc');
  const assetsSort = useTableSort('assets_page', 'name', 'asc');

  // Saved filters
  const { savedFilters, saveCurrentFilters, saveFilterPreset, deleteFilterPreset, loadCurrentFilters } = useSavedFilters('rooms_assets_page');

  // Column visibility for Assets tab
  const assetsColumns = [
    { key: 'select', label: '' },
    { key: 'name', label: t('assets.assetName') },
    { key: 'serial', label: t('assets.serial') },
    { key: 'category', label: t('assets.category') },
    { key: 'room', label: t('common.room') },
    { key: 'status', label: t('common.status') },
    { key: 'actions', label: t('common.actions') },
  ];
  const assetsSettings = useTableSettings('assets_cols', assetsColumns, ['name', 'serial', 'room']);

  // Room form
  const [roomDialog, setRoomDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomForm, setRoomForm] = useState({ branch_id: '', name: '', code: '', status: 'active' });

  // Asset form
  const [assetDialog, setAssetDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetForm, setAssetForm] = useState({ branch_id: '', room_id: '', name: '', category: '', status: 'operational', serial: '', model: '' });

  // QR Print state
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  // Load saved filters on mount
  useEffect(() => {
    const saved = loadCurrentFilters();
    if (saved) {
      if (saved.branch) setFBranch(saved.branch);
      if (saved.tab) setTab(saved.tab);
      if (saved.category) setFCategory(saved.category);
      if (saved.status) setFStatus(saved.status);
      if (saved.room) setFRoom(saved.room);
    }
  }, []);

  // Save current filters when they change
  useEffect(() => {
    saveCurrentFilters({ branch: fBranch, tab, category: fCategory, status: fStatus, room: fRoom });
  }, [fBranch, tab, fCategory, fStatus, fRoom, saveCurrentFilters]);

  useEffect(() => {
    const init = async () => {
      try {
        const [branchRes, roomRes, assetRes] = await Promise.all([
          api.get('/branches'),
          api.get('/rooms'),
          api.get('/assets'),
        ]);
        setBranches(branchRes.data);
        setRooms(roomRes.data);
        setAssets(assetRes.data);
      } catch (err) { toast.error(t('toast.error')); }
      finally { setLoading(false); }
    };
    init();
  }, [t]);

  useEffect(() => {
    if (fBranch === 'all') return;
    const loadFiltered = async () => {
      try {
        const params = { branch_id: fBranch };
        const [roomRes, assetRes] = await Promise.all([
          api.get('/rooms', { params }),
          api.get('/assets', { params }),
        ]);
        setRooms(roomRes.data);
        setAssets(assetRes.data);
      } catch (err) { toast.error(t('toast.error')); }
    };
    loadFiltered();
  }, [fBranch, t]);

  const loadRoomsAssets = async () => {
    try {
      const params = fBranch !== 'all' ? { branch_id: fBranch } : {};
      const [roomRes, assetRes] = await Promise.all([
        api.get('/rooms', { params }),
        api.get('/assets', { params }),
      ]);
      setRooms(roomRes.data);
      setAssets(assetRes.data);
    } catch (err) { toast.error(t('toast.error')); }
  };

  // Filtered and sorted data
  const filteredRooms = useMemo(() => {
    let result = rooms;
    if (fSearch) {
      const q = fSearch.toLowerCase();
      result = result.filter(r => r.name.toLowerCase().includes(q) || (r.code && r.code.toLowerCase().includes(q)));
    }
    return roomsSort.sortData(result);
  }, [rooms, fSearch, roomsSort]);

  const filteredAssets = useMemo(() => {
    let result = assets;
    if (fSearch) {
      const q = fSearch.toLowerCase();
      result = result.filter(a => 
        a.name.toLowerCase().includes(q) || 
        (a.serial && a.serial.toLowerCase().includes(q)) ||
        (a.category && a.category.toLowerCase().includes(q))
      );
    }
    if (fCategory !== 'all') {
      result = result.filter(a => a.category === fCategory);
    }
    if (fStatus !== 'all') {
      result = result.filter(a => a.status === fStatus);
    }
    if (fRoom !== 'all') {
      result = result.filter(a => a.room_id === fRoom);
    }
    return assetsSort.sortData(result);
  }, [assets, fSearch, fCategory, fStatus, fRoom, assetsSort]);

  // Asset selection for QR printing
  const assetSelection = useTableSelection(filteredAssets);

  // Room CRUD
  const handleRoomSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRoom) {
        await api.put(`/rooms/${editingRoom.id}`, { name: roomForm.name, code: roomForm.code, status: roomForm.status });
      } else {
        await api.post('/rooms', roomForm);
      }
      toast.success(t('toast.saved'));
      setRoomDialog(false);
      setEditingRoom(null);
      loadRoomsAssets();
    } catch (err) { toast.error(err.response?.data?.detail || t('toast.error')); }
  };

  const deleteRoom = async (id) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try { await api.delete(`/rooms/${id}`); toast.success(t('toast.saved')); loadRoomsAssets(); }
    catch (err) { toast.error(t('toast.error')); }
  };

  // Asset CRUD
  const handleAssetSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        const { branch_id, ...rest } = assetForm;
        await api.put(`/assets/${editingAsset.id}`, rest);
      } else {
        await api.post('/assets', assetForm);
      }
      toast.success(t('toast.saved'));
      setAssetDialog(false);
      setEditingAsset(null);
      loadRoomsAssets();
    } catch (err) { toast.error(err.response?.data?.detail || t('toast.error')); }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try { await api.delete(`/assets/${id}`); toast.success(t('toast.saved')); loadRoomsAssets(); }
    catch (err) { toast.error(t('toast.error')); }
  };

  const branchName = (id) => branches.find(b => b.id === id)?.name || id;
  const roomName = (id) => rooms.find(r => r.id === id)?.name || id;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('assetDetail.copied'));
  };

  const getSelectedAssetsForPrint = () => {
    return assetSelection.selectedItems.map(a => ({
      ...a,
      branch_name: branchName(a.branch_id),
    }));
  };

  const applyFilterPreset = (filters) => {
    if (filters.branch) setFBranch(filters.branch);
    if (filters.tab) setTab(filters.tab);
    if (filters.category) setFCategory(filters.category);
    if (filters.status) setFStatus(filters.status);
    if (filters.room) setFRoom(filters.room);
    toast.success(t('filters.applied'));
  };

  const clearFilters = () => {
    setFBranch('all');
    setFSearch('');
    setFCategory('all');
    setFStatus('all');
    setFRoom('all');
  };

  const hasActiveFilters = fBranch !== 'all' || fSearch || fCategory !== 'all' || fStatus !== 'all' || fRoom !== 'all';

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div data-testid="rooms-assets-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{t('rooms.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filteredRooms.length} {t('rooms.roomsTab').toLowerCase()}, {filteredAssets.length} {t('rooms.assetsTab').toLowerCase()}
          </p>
        </div>
        <div className="flex gap-2">
          <SavedFiltersDropdown
            savedFilters={savedFilters}
            onSave={saveFilterPreset}
            onApply={applyFilterPreset}
            onDelete={deleteFilterPreset}
            currentFilters={{ branch: fBranch, tab, category: fCategory, status: fStatus, room: fRoom }}
            t={t}
          />
          <Select value={fBranch} onValueChange={setFBranch}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="ra-branch-filter"><SelectValue placeholder={t('common.all') + ' ' + t('common.branches')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="rooms" data-testid="tab-rooms" className="data-[state=active]:bg-card data-[state=active]:text-brand">
            <DoorOpen className="w-4 h-4 me-2" /> {t('rooms.roomsTab')}
            <Badge variant="secondary" className="ms-2">{filteredRooms.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets" className="data-[state=active]:bg-card data-[state=active]:text-brand">
            <Box className="w-4 h-4 me-2" /> {t('rooms.assetsTab')}
            <Badge variant="secondary" className="ms-2">{filteredAssets.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* ROOMS */}
        <TabsContent value="rooms" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('common.search')}
                value={fSearch}
                onChange={(e) => setFSearch(e.target.value)}
                className="ps-9"
                data-testid="rooms-search"
              />
              {fSearch && (
                <button onClick={() => setFSearch('')} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                <X className="w-4 h-4 me-1" /> {t('filters.resetFilters')}
              </Button>
            )}
            <Dialog open={roomDialog} onOpenChange={setRoomDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-brand hover:bg-brand-hover text-brand-foreground" onClick={() => { setEditingRoom(null); setRoomForm({ branch_id: fBranch !== 'all' ? fBranch : '', name: '', code: '', status: 'active' }); }} data-testid="create-room-btn">
                  <Plus className="w-4 h-4 me-1" /> {t('rooms.createRoom')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-barlow text-xl">{editingRoom ? t('rooms.editRoom') : t('rooms.createRoom')}</DialogTitle></DialogHeader>
                <form onSubmit={handleRoomSubmit} className="space-y-4" data-testid="room-form">
                  {!editingRoom && (
                    <div className="space-y-2">
                      <Label>{t('common.branch')} *</Label>
                      <Select value={roomForm.branch_id} onValueChange={v => setRoomForm({ ...roomForm, branch_id: v })}>
                        <SelectTrigger data-testid="room-branch-select"><SelectValue placeholder={t('workRequest.selectBranch')} /></SelectTrigger>
                        <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>{t('rooms.roomName')} *</Label>
                    <Input value={roomForm.name} onChange={e => setRoomForm({ ...roomForm, name: e.target.value })} required data-testid="room-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('rooms.roomCode')}</Label>
                    <Input value={roomForm.code} onChange={e => setRoomForm({ ...roomForm, code: e.target.value })} placeholder={t('rooms.roomCodePlaceholder')} data-testid="room-code-input" />
                  </div>
                  <Button type="submit" className="w-full bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="room-submit-btn">{editingRoom ? t('common.update') : t('common.create')}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="bg-card border-border shadow-sm">
            <CardContent className="p-0">
              <EnhancedTable stickyHeader>
                <StickyTableHeader>
                  <tr className="border-b border-border">
                    <SortableHeader field="name" currentField={roomsSort.sortField} currentDir={roomsSort.sortDir} onSort={roomsSort.toggleSort}>
                      {t('common.name')}
                    </SortableHeader>
                    <SortableHeader field="code" currentField={roomsSort.sortField} currentDir={roomsSort.sortDir} onSort={roomsSort.toggleSort}>
                      {t('rooms.code')}
                    </SortableHeader>
                    <TableHeader>{t('common.branch')}</TableHeader>
                    <SortableHeader field="status" currentField={roomsSort.sortField} currentDir={roomsSort.sortDir} onSort={roomsSort.toggleSort}>
                      {t('common.status')}
                    </SortableHeader>
                    <TableHeader align="end">{t('common.actions')}</TableHeader>
                  </tr>
                </StickyTableHeader>
                <EnhancedTableBody>
                  {filteredRooms.length === 0 && (
                    <TableEmptyState icon={DoorOpen} title={t('rooms.noRooms')} colSpan={5} />
                  )}
                  {filteredRooms.map(r => (
                    <EnhancedTableRow
                      key={r.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/rooms/${r.id}`)}
                      data-testid={`room-row-${r.id}`}
                    >
                      <EnhancedTableCell className="font-medium text-foreground">{r.name}</EnhancedTableCell>
                      <EnhancedTableCell>
                        {r.code ? (
                          <div className="flex items-center gap-1">
                            <span className="font-mono text-xs text-brand">{r.code}</span>
                            <button onClick={(e) => { e.stopPropagation(); copyToClipboard(r.code); }} className="p-1 hover:bg-secondary rounded">
                              <Copy className="w-3 h-3 text-muted-foreground" />
                            </button>
                          </div>
                        ) : <span className="text-muted-foreground">—</span>}
                      </EnhancedTableCell>
                      <EnhancedTableCell className="text-muted-foreground text-xs">{branchName(r.branch_id)}</EnhancedTableCell>
                      <EnhancedTableCell>
                        <Badge variant={r.status === 'active' ? 'default' : 'secondary'} className={r.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : ''}>
                          {t(`common.${r.status}`)}
                        </Badge>
                      </EnhancedTableCell>
                      <EnhancedTableCell align="end" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/rooms/${r.id}`); }}>
                          <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditingRoom(r); setRoomForm({ branch_id: r.branch_id, name: r.name, code: r.code || '', status: r.status }); setRoomDialog(true); }}>
                          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteRoom(r.id); }}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                        </Button>
                      </EnhancedTableCell>
                    </EnhancedTableRow>
                  ))}
                </EnhancedTableBody>
              </EnhancedTable>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ASSETS */}
        <TabsContent value="assets" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('common.search')}
                value={fSearch}
                onChange={(e) => setFSearch(e.target.value)}
                className="ps-9"
                data-testid="assets-search"
              />
              {fSearch && (
                <button onClick={() => setFSearch('')} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={fCategory} onValueChange={setFCategory}>
              <SelectTrigger className="w-full sm:w-[140px]" data-testid="assets-category-filter"><SelectValue placeholder={t('assets.category')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                {ASSET_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fStatus} onValueChange={setFStatus}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="assets-status-filter"><SelectValue placeholder={t('common.status')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                {ASSET_STATUSES.map(s => <SelectItem key={s} value={s}>{t(`assets.${s}`)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fRoom} onValueChange={setFRoom}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="assets-room-filter"><SelectValue placeholder={t('common.room')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')} {t('common.rooms')}</SelectItem>
                {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <TableSettingsDropdown
              columns={assetsColumns}
              lockedColumns={['name', 'serial', 'room']}
              density={assetsSettings.density}
              onToggleColumn={assetsSettings.toggleColumn}
              onSetDensity={assetsSettings.setDensity}
              onResetAll={assetsSettings.resetAll}
              isVisible={assetsSettings.isVisible}
              isLocked={assetsSettings.isLocked}
              t={t}
            />
            {assetSelection.selectedCount > 0 && (
              <div className="flex items-center gap-3">
                <Badge variant="secondary">{assetSelection.selectedCount} {t('qr.assetsSelected')}</Badge>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setQrDialogOpen(true)}
                  data-testid="print-qr-btn"
                >
                  <QrCode className="w-4 h-4 me-1" />
                  {t('qr.printLabels')}
                </Button>
                <Button variant="ghost" size="sm" onClick={assetSelection.clearSelection} className="text-muted-foreground">
                  {t('bulk.clearSelection')}
                </Button>
              </div>
            )}
            {hasActiveFilters && !assetSelection.selectedCount && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                <X className="w-4 h-4 me-1" /> {t('filters.resetFilters')}
              </Button>
            )}
            <Dialog open={assetDialog} onOpenChange={setAssetDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-brand hover:bg-brand-hover text-brand-foreground" onClick={() => { setEditingAsset(null); setAssetForm({ branch_id: fBranch !== 'all' ? fBranch : '', room_id: '', name: '', category: '', status: 'operational', serial: '', model: '' }); }} data-testid="create-asset-btn">
                  <Plus className="w-4 h-4 me-1" /> {t('assets.createAsset')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-barlow text-xl">{editingAsset ? t('assets.editAsset') : t('assets.createAsset')}</DialogTitle></DialogHeader>
                <form onSubmit={handleAssetSubmit} className="space-y-4" data-testid="asset-form">
                  {!editingAsset && (
                    <>
                      <div className="space-y-2">
                        <Label>{t('common.branch')} *</Label>
                        <Select value={assetForm.branch_id} onValueChange={v => setAssetForm({ ...assetForm, branch_id: v, room_id: '' })}>
                          <SelectTrigger data-testid="asset-branch-select"><SelectValue placeholder={t('workRequest.selectBranch')} /></SelectTrigger>
                          <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('common.room')}</Label>
                        <Select value={assetForm.room_id || 'none'} onValueChange={v => setAssetForm({ ...assetForm, room_id: v === 'none' ? '' : v })}>
                          <SelectTrigger data-testid="asset-room-select"><SelectValue placeholder={t('common.optional')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('common.none')}</SelectItem>
                            {rooms.filter(r => r.branch_id === assetForm.branch_id).map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    <Label>{t('common.name')} *</Label>
                    <Input value={assetForm.name} onChange={e => setAssetForm({ ...assetForm, name: e.target.value })} required data-testid="asset-name-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t('assets.category')} *</Label>
                      <Input value={assetForm.category} onChange={e => setAssetForm({ ...assetForm, category: e.target.value })} required data-testid="asset-category-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('common.status')}</Label>
                      <Select value={assetForm.status} onValueChange={v => setAssetForm({ ...assetForm, status: v })}>
                        <SelectTrigger data-testid="asset-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="operational">{t('assets.operational')}</SelectItem>
                          <SelectItem value="needs_maintenance">{t('assets.needsMaintenance')}</SelectItem>
                          <SelectItem value="out_of_service">{t('assets.outOfService')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t('assets.serial')}</Label>
                      <Input value={assetForm.serial} onChange={e => setAssetForm({ ...assetForm, serial: e.target.value })} data-testid="asset-serial-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('assets.model')}</Label>
                      <Input value={assetForm.model} onChange={e => setAssetForm({ ...assetForm, model: e.target.value })} data-testid="asset-model-input" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="asset-submit-btn">{editingAsset ? t('common.update') : t('common.create')}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="bg-card border-border shadow-sm">
            <CardContent className="p-0">
              <EnhancedTable stickyHeader>
                <StickyTableHeader>
                  <tr className="border-b border-border">
                    <th className="w-12 px-3 py-3">
                      <Checkbox 
                        checked={assetSelection.isAllSelected}
                        indeterminate={assetSelection.isSomeSelected}
                        onCheckedChange={assetSelection.toggleSelectAll}
                        data-testid="select-all-assets"
                      />
                    </th>
                    <SortableHeader field="name" currentField={assetsSort.sortField} currentDir={assetsSort.sortDir} onSort={assetsSort.toggleSort}>
                      {t('common.name')}
                    </SortableHeader>
                    <SortableHeader field="serial" currentField={assetsSort.sortField} currentDir={assetsSort.sortDir} onSort={assetsSort.toggleSort}>
                      {t('assets.serial')}
                    </SortableHeader>
                    <SortableHeader field="category" currentField={assetsSort.sortField} currentDir={assetsSort.sortDir} onSort={assetsSort.toggleSort}>
                      {t('assets.category')}
                    </SortableHeader>
                    <TableHeader>{t('common.room')}</TableHeader>
                    <SortableHeader field="status" currentField={assetsSort.sortField} currentDir={assetsSort.sortDir} onSort={assetsSort.toggleSort}>
                      {t('common.status')}
                    </SortableHeader>
                    <TableHeader align="end">{t('common.actions')}</TableHeader>
                  </tr>
                </StickyTableHeader>
                <EnhancedTableBody>
                  {filteredAssets.length === 0 && (
                    <TableEmptyState icon={Box} title={t('assets.noAssets')} colSpan={7} />
                  )}
                  {filteredAssets.map(a => (
                    <EnhancedTableRow
                      key={a.id}
                      isSelected={assetSelection.isSelected(a.id)}
                      data-testid={`asset-row-${a.id}`}
                    >
                      <EnhancedTableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={assetSelection.isSelected(a.id)}
                          onCheckedChange={() => assetSelection.toggleSelect(a.id)}
                        />
                      </EnhancedTableCell>
                      <EnhancedTableCell className="font-medium text-foreground cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>
                        {a.name}
                      </EnhancedTableCell>
                      <EnhancedTableCell>
                        {a.serial ? (
                          <div className="flex items-center gap-1">
                            <span className="font-mono text-xs text-brand">{a.serial}</span>
                            <button onClick={(e) => { e.stopPropagation(); copyToClipboard(a.serial); }} className="p-1 hover:bg-secondary rounded">
                              <Copy className="w-3 h-3 text-muted-foreground" />
                            </button>
                          </div>
                        ) : <span className="text-muted-foreground">—</span>}
                      </EnhancedTableCell>
                      <EnhancedTableCell className="text-muted-foreground">{a.category}</EnhancedTableCell>
                      <EnhancedTableCell className="text-muted-foreground text-xs">{a.room_id ? roomName(a.room_id) : '—'}</EnhancedTableCell>
                      <EnhancedTableCell>
                        <Badge variant={a.status === 'operational' ? 'default' : a.status === 'needs_maintenance' ? 'secondary' : 'destructive'}
                               className={a.status === 'operational' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 
                                          a.status === 'needs_maintenance' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 
                                          'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'}>
                          {t(`assets.${a.status}`)}
                        </Badge>
                      </EnhancedTableCell>
                      <EnhancedTableCell align="end" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/assets/${a.id}`)}>
                          <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setEditingAsset(a); setAssetForm({ branch_id: a.branch_id, room_id: a.room_id || '', name: a.name, category: a.category, status: a.status, serial: a.serial || '', model: a.model || '' }); setAssetDialog(true); }}>
                          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteAsset(a.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                        </Button>
                      </EnhancedTableCell>
                    </EnhancedTableRow>
                  ))}
                </EnhancedTableBody>
              </EnhancedTable>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* QR Label Print Dialog */}
      <QRLabelPrint 
        assets={getSelectedAssetsForPrint()}
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
      />
    </div>
  );
}
